import { useEffect } from "react";
import { PaymentDueAccountVM, PaymentMethodTypesEnum, PaymentMethodVM, PaymentTypesEnum } from "@propertelligent/client-api";
import { useFormContext } from "react-hook-form";
import "./payment.css";
import PaymentSummaryTotalAmount from "./PaymentSummaryTotalAmount";
import H3 from "../typography/H3";
import SelectField from "../form-fields/SelectField";
import InputField from "../form-fields/InputField";
import EmptyState from "../emptystate";
import { GetImages } from "../../utils/GetImages";
import { getEnumOptions } from "../../utils/enumHelpers";

// Payment recording modes -- mirrors PaymentMethodTypesEnum on the backend.
const PAYMENT_MODE = PaymentMethodTypesEnum;

interface PaymentSummaryProps {
  totalAmount: string;
  paymentMethods: PaymentMethodVM[];
  Payments: PaymentDueAccountVM[];
  // Assisted is only valid for tenant receivables — callers on the payables side omit this.
  showAssisted?: boolean;
  // Plaid (online transfer) is not a valid mode when the landlord records a receivable payment — those are
  // recorded receipts (Cash/Check/Assisted/Other). Callers on the receivable side pass false to hide it.
  showPlaid?: boolean;
}

export default function PaymentSummary({
  totalAmount,
  paymentMethods,
  Payments,
  showAssisted = false,
  showPlaid = true,
}: PaymentSummaryProps) {
  const { watch, resetField, trigger } = useFormContext();
  const selectedMode = Number(watch("paymentMethodTypeId"));

  // Each conditional field below (PaymentModeId/checkNum/transactionId/assistanceProvider) is
  // registered via a Controller/useController with the RHF default shouldUnregister: false, so
  // once one of them has registered a "required" error it keeps contributing to
  // formState.isValid even after it unmounts (e.g. user briefly lands on Check, leaves checkNum
  // empty, then settles on Cash) -- permanently blocking Save/Pay for modes that have no unmet
  // requirements of their own. Reset all of them on every mode change so only the field relevant
  // to the newly selected mode is (re)registered fresh. Mirrors the same guard already used in
  // ServiceRequestDetails.tsx's local payment form. formState.isValid is not always recomputed
  // synchronously after resetField on the very first mode change (e.g. selecting Cash as the
  // first-ever mode left Save disabled) -- explicitly re-trigger validation so it updates
  // immediately regardless of selection order.
  useEffect(() => {
    resetField("PaymentModeId", { keepError: false });
    resetField("checkNum", { keepError: false });
    resetField("transactionId", { keepError: false });
    resetField("assistanceProvider", { keepError: false });
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMode]);


  // A Propertelligent subscription bill is a payable owed by the organization to the platform, not an
  // offline receipt collected from someone else -- Cash/Check/Assisted/Other manual recording modes don't
  // apply to it, only an electronic transfer from the organization's own connected Plaid account does
  // (mirrors the backend guard in PaymentDueAccountService.PayPaymentDueAccountCoreAsync).
  const hasSubscriptionPayment = Payments.some(
    (payment) => payment.paymentTypeId === PaymentTypesEnum.PropertelligentSubscription,
  );

  const paymentModeOptions = hasSubscriptionPayment
    ? getEnumOptions(PAYMENT_MODE, [PAYMENT_MODE.Plaid])
    : getEnumOptions(PAYMENT_MODE, [
        ...(showPlaid ? [PAYMENT_MODE.Plaid] : []),
        PAYMENT_MODE.Cash,
        PAYMENT_MODE.Check,
        ...(showAssisted ? [PAYMENT_MODE.Assisted] : []),
        PAYMENT_MODE.Other,
      ]);

  const savedMethodOptions = paymentMethods
    .filter((method) => method.paymentMethodTypeId === PAYMENT_MODE.Plaid)
    .map((method) => ({
      Id: method.id,
      Name: method.accountName,
    }));

  return (
    <div className="main-container main-container-payment">
      <div className="header-section">
        <H3 text="Payment Summary" />
      </div>
      {Payments.length > 0 ? (
        <>
          <PaymentSummaryTotalAmount label="You've Paid" amount={totalAmount} />

          <div className="mx-4 my-3">
            {hasSubscriptionPayment && (
              <p className="text-muted mb-2">
                Propertelligent subscription payments can only be made with a connected Plaid payment account.
              </p>
            )}

            <div className="row mt-2 flex-column">
              {/* Plain flexbox, not Bootstrap's .row/.col-* -- explicit inline flex-basis so
                  Payment Mode is always the same width as Payment Date below it (~50%),
                  whether or not a mode-specific field sits beside it, instead of being left to
                  size off Bootstrap's grid math nested inside a flex-column parent. */}
              <div className="d-flex gap-3 mb-1" style={{ width: "100%" }}>
                <div style={{ flex: "1 1 0", maxWidth: "50%", minWidth: 0 }}>
                  <SelectField
                    placeholderText="Select Payment Mode"
                    name="paymentMethodTypeId"
                    label="Payment Mode"
                    required
                    requiredMessage="Payment Mode is required"
                    options={paymentModeOptions}
                  />
                </div>

                {selectedMode === PAYMENT_MODE.Plaid && (
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <SelectField
                      placeholderText="Select Payment Method"
                      name="PaymentModeId"
                      label="Payment Method"
                      required
                      requiredMessage="Payment Method is required"
                      options={savedMethodOptions}
                    />
                  </div>
                )}

                {selectedMode === PAYMENT_MODE.Check && (
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <InputField
                      name="checkNum"
                      label="Check No"
                      placeholder="Enter Check No"
                      type="text"
                      required
                      requiredMessage="Check No is required"
                    />
                  </div>
                )}

                {selectedMode === PAYMENT_MODE.Other && (
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <InputField
                      name="transactionId"
                      label="Transaction ID"
                      placeholder="Enter Transaction ID"
                      type="text"
                      required
                      requiredMessage="Transaction ID is required"
                    />
                  </div>
                )}

                {selectedMode === PAYMENT_MODE.Assisted && (
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <InputField
                      name="assistanceProvider"
                      label="Assistance Provider"
                      placeholder="Enter Assistance Provider"
                      type="text"
                      required
                      requiredMessage="Assistance Provider is required"
                      allowSpecialChars={true}
                    />
                  </div>
                )}
              </div>

              <div className="col-md-6 mb-1">
                <InputField
                  name="date"
                  label="Payment Date"
                  type="date"
                  required
                  requiredMessage="Payment Date is required"
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <EmptyState
          icon={GetImages.emptyStatePayment}
          heading="No Payment Selected"
          isButton={false}
          className="empty-state-wrapper"
        />
      )
      }
    </div>
  );
}
